import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Tooltip from "@mui/material/Tooltip";
import FeedbackIcon from "@mui/icons-material/Feedback";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../store/store";
import { createCustomerPortalSession } from "../store/modules/subscription/subscriptionActions";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SettingsIcon from "@mui/icons-material/Settings";
import WelcomeName from "./WelcomeName";
import UserAccountMenu from "./UserAccountMenu";
import BarChartIcon from "@mui/icons-material/BarChart";
import MapIcon from "@mui/icons-material/Map";
import { RootState } from "../store/reducers";
import { getUser } from "../store/modules/user/userActions";

import ImportExportIcon from "@mui/icons-material/ImportExport";
import RepeatIcon from "@mui/icons-material/Repeat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LaunchIcon from "@mui/icons-material/Launch";
import TrafficIcon from "@mui/icons-material/Traffic";
import useMediaQuery from "@mui/material/useMediaQuery";
import Divider from "@mui/material/Divider";
import {
  getCommonData,
  getUserCustomData,
} from "../store/modules/common/commonActions";
import { fetchTransactionYears } from "../store/modules/transactionYear/transactionYearActions";
import {
  setHasHandledImpersonation,
  setHasHandledStopImpersonation,
} from "../store/modules/admin/adminStateActions";
import { getBrokerageAnniversaries } from "../store/modules/financialSettings/financialSettingsActions";
import { getHiddenFields } from "../store/modules/hiddenFields/hiddenFieldActions";

const NavBar = ({
  children,
  title,
}: {
  children?: React.ReactNode;
  title?: string;
}) => {
  const theme = useTheme();
  const { currentUser: user } = useSelector((state: RootState) => state?.user);
  const { isImpersonating, impersonatedUserEmail, status } = useSelector(
    (state: RootState) => state.admin,
  );
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { hasHandledImpersonation, hasHandledStopImpersonation } = useSelector(
    (state: RootState) => state.admin,
  );
  const navigate = useNavigate();
  const drawerWidth = 130;

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  useEffect(() => {
    if (status === "Impersonated" && !hasHandledImpersonation) {
      dispatch(getUserCustomData());
      dispatch(fetchTransactionYears());
      dispatch(getCommonData());
      dispatch(getBrokerageAnniversaries());
      dispatch(getHiddenFields());
      dispatch(setHasHandledImpersonation());
      if (window.location.pathname !== "/") {
        navigate("/");
      }
    } else if (status === "Stopped" && !hasHandledStopImpersonation) {
      dispatch(getUserCustomData());
      dispatch(getUser());
      dispatch(getCommonData());
      dispatch(fetchTransactionYears());
      dispatch(getBrokerageAnniversaries());
      dispatch(getHiddenFields());

      dispatch(setHasHandledStopImpersonation());

      if (window.location.pathname !== "/") {
        navigate("/");
      }
    }
  }, [status]);

  const handleFeedbackClick = () => {
    const subject = encodeURIComponent("AGENTS INTEL- Feedback & Suggestions");
    const body = encodeURIComponent(`Hi,

I have feedback/suggestions for the AGENTS INTEL system:


Thank you!`);

    window.location.href = `mailto:support@agentsintel.com?subject=${subject}&body=${body}`;
  };

  const isUserSubscribed =
    user && user.subscriptionId && user.subscriptionId !== undefined
      ? true
      : false;
  const navItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/" },
    {
      text: "Search & Map",
      icon: <MapIcon />,
      path: "/transactions/search-map",
    },
    {
      text: "Business Zones",
      icon: <TrafficIcon />,
      path: "/business-zones",
    },
    { text: "Analytics", icon: <BarChartIcon />, path: "/analytics" },
    {
      text: "Repeats & Referrers",
      icon: <RepeatIcon />,
      path: "/repeats-and-referrers",
    },
    {
      text: "Financial Trends",
      icon: <TrendingUpIcon />,
      path: "/financial-trends",
    },
    {
      text: "Business Planning",
      icon: <AssignmentIcon />,
      path: "/business-planning",
    },

    {
      text: "Import/Export",
      icon: <ImportExportIcon />,
      path: "/import",
    },
    { text: "Settings", icon: <SettingsIcon />, path: "/settings" },

    {
      text: "Subscription",
      icon: <ReceiptIcon />,
      onClick: !isUserSubscribed
        ? () => {
            dispatch(getUser());
          }
        : () => {
            if (user && user.subscriptionId) {
              dispatch(createCustomerPortalSession());
            }
          },
      path: !isUserSubscribed ? "/pricing" : null,
    },
    {
      text: "Resources",
      icon: <LaunchIcon />,
      path: null,
      onClick: () => {
        window.open("https://agentsintel.com/resources/", "_blank");
      },
    },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: (theme) =>
            `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
          borderBottom: "none",
        }}
      >
        <Toolbar sx={{ minHeight: 64, px: { xs: 1, sm: 3 } }}>
          {isMobileOrTablet && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: "0.5px",
              color: "white",
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            {title || "AGENTS INTEL"}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <WelcomeName
              isImpersonating={isImpersonating}
              impersonatedUserEmail={impersonatedUserEmail}
            />
            <Tooltip title="Send Feedback & Suggestions" placement="bottom">
              <IconButton
                onClick={handleFeedbackClick}
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <FeedbackIcon />
              </IconButton>
            </Tooltip>
            <UserAccountMenu
              isImpersonating={isImpersonating}
              impersonatedUserEmail={impersonatedUserEmail}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer for navigation */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: "none",
          },
        }}
        variant={isMobileOrTablet ? "temporary" : "permanent"}
        anchor="left"
        open={isMobileOrTablet ? mobileDrawerOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(0, 1),
            ...theme.mixins.toolbar,
            justifyContent: "flex-end",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <IconButton size="small" sx={{ color: theme.palette.primary.main }}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </Box>
        <List sx={{ pt: 1 }}>
          {navItems
            //.filter((item) => !item.adminOnly || user?.isAdmin === true)
            .map((item, index) => (
              <React.Fragment key={item.text}>
                {item.text === "Resources" && <Divider sx={{ my: 1 }} />}
                <ListItem disablePadding>
                  <ListItemButton
                    component={item.path ? RouterLink : "div"}
                    to={item.path ?? "#"}
                    onClick={() => {
                      if (item.onClick) {
                        item.onClick();
                      }
                      if (isMobileOrTablet) {
                        setMobileDrawerOpen(false);
                      }
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "16px 8px",
                      gap: "8px",
                      textAlign: "center",
                      position: "relative",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(26, 54, 93, 0.08)",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "20%",
                          height: "60%",
                          width: 3,
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: "0 4px 4px 0",
                        },
                      },
                      "& .MuiListItemIcon-root": {
                        minWidth: "auto",
                        color: theme.palette.primary.main,
                        opacity: 0.85,
                        transition: "all 0.2s",
                        margin: 0,
                      },
                      "& .MuiListItemText-root": {
                        margin: 0,
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginLeft: isMobileOrTablet ? 0 : `${drawerWidth}px`,
          width: isMobileOrTablet ? "100%" : `calc(100% - ${drawerWidth}px)`,
          maxWidth: isMobileOrTablet ? "100%" : `calc(100% - ${drawerWidth}px)`,
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(0, 2),
            ...theme.mixins.toolbar,
          }}
        />
        <Box sx={{ padding: "24px", minHeight: "calc(100vh - 64px)" }}>
          {children}
        </Box>
      </Box>
    </>
  );
};

export default NavBar;
